import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
const AIAssistant = lazy(() => import('./AIAssistant'));
import {
  MessageCircle,
  Phone,
  Menu,
  X,
  Instagram,
  Facebook,
  Youtube,
  Linkedin,
  Star,
  Globe
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SOCIAL_LINKS, V } from '../constants';
import { trackEvent, ANALYTICS_EVENTS, trackWhatsAppClick } from '../lib/analytics';
import WhatsAppIcon from './WhatsAppIcon';

/* ================================
   Social Links Component
================================ */
function SocialConnect() {
  return (
    <div className="flex gap-4">
      {[{
        href: SOCIAL_LINKS.instagram,
        label: 'Instagram',
        icon: <Instagram size={18} />
      }, {
        href: SOCIAL_LINKS.facebook,
        label: 'Facebook',
        icon: <Facebook size={18} />
      }, {
        href: SOCIAL_LINKS.youtube,
        label: 'YouTube',
        icon: <Youtube size={18} />
      }, {
        href: SOCIAL_LINKS.linkedin,
        label: 'LinkedIn',
        icon: <Linkedin size={18} />
      }].map((item, i) => (
        <a
          key={i}
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={item.label}
          className="w-11 h-11 rounded-full bg-white/5 flex items-center justify-center hover:bg-gold hover:text-bg transition-all border border-white/5"
        >
          {item.icon}
        </a>
      ))}
    </div>
  );
}

/* ================================
   Layout Component
================================ */
export default function Layout({ children }: { children: React.ReactNode }) {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Portfolio', path: '/portfolio' },
    { name: 'Blog', path: '/blog' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-bg text-ink2 selection:bg-gold/30 selection:text-gold">

      {/* ================= HEADER ================= */}
      <header className="sticky top-0 z-50 bg-bg/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 h-24 flex items-center justify-between">

          {/* Logo with tagline */}
          <Link to="/" className="flex items-center gap-4 group">
            <img
              src="https://images.pixieset.com/492772111/2c35f543e5662c954e9dea8a7381893c-large.png"
              alt="Faraja Media Branding"
              width="56"
              height="56"
              loading="eager"
              fetchPriority="high"
              className="object-contain transition-transform duration-500 group-hover:scale-105"
            />
            <div className="flex flex-col">
              <span className="text-[20px] font-serif font-bold tracking-tight text-gold group-hover:text-gold/80 transition-colors leading-none">
                FARAJA MEDIA
              </span>
              <span className="text-xs uppercase tracking-[0.3em] text-ink2 font-sans leading-none mt-1">
                Helping You Be Present
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-10">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-[11px] font-bold tracking-[0.2em] uppercase transition-all duration-300 min-h-[48px] min-w-[48px] inline-flex items-center justify-center ${location.pathname === link.path ? 'text-gold' : 'hover:text-gold'
                  }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-8">
            <a
              href="tel:+254721296564"
              onClick={() => trackEvent(ANALYTICS_EVENTS.PHONE_CLICK, { location: 'Header' })}
              className="flex items-center gap-3 text-[12px] font-bold text-ink hover:text-gold transition-colors uppercase tracking-widest"
            >
              <Phone size={14} className="text-gold" />
              +254 721 296 564
            </a>
            <Link to="/booking" className="btn-primary px-8 py-3.5 text-[11px] uppercase tracking-[0.2em] shadow-xl shadow-gold/10">
              Book Now
            </Link>
          </div>

          {/* Mobile Toggle – dynamic aria-label */}
          <button
            className="lg:hidden w-12 h-12 flex items-center justify-center text-ink hover:bg-white/5 rounded-full transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* ================= MOBILE MENU (Animated) ================= */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-0 z-[60] bg-bg flex flex-col lg:hidden"
          >
            <div className="h-24 px-6 flex items-center justify-between border-b border-white/5">
              <span className="text-gold font-serif text-xl font-bold">FARAJA MEDIA</span>
              <button onClick={() => setIsMenuOpen(false)} className="p-2 text-ink" aria-label="Close menu">
                <X size={24} />
              </button>
            </div>
            <nav className="flex-grow flex flex-col justify-center items-center gap-10 p-6">
              {navLinks.map((link, i) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Link
                    to={link.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`text-4xl font-serif ${location.pathname === link.path ? 'text-gold' : 'text-ink'}`}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: navLinks.length * 0.1 }}
                className="pt-16 flex flex-col items-center gap-8 w-full max-w-xs"
              >
                <a href="tel:+254721296564" className="flex items-center gap-4 text-xl text-ink font-serif">
                  <Phone className="text-gold" />
                  +254 721 296 564
                </a>
                <Link
                  to="/booking"
                  onClick={() => setIsMenuOpen(false)}
                  className="btn-primary w-full py-5 text-[12px] uppercase tracking-[0.2em]"
                >
                  Book Your Coverage
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ================= MAIN ================= */}
      <main className="flex-grow min-h-[calc(100vh-400px)]">
        {children}
      </main>

      {/* ================= FOOTER (Full version) ================= */}
      <footer className="bg-deep border-t border-white/5 pt-32 pb-12" style={{ minHeight: '600px', contain: 'layout' }}>
        <div className="max-w-7xl mx-auto px-6">
          {/* Internal Linking – Services */}
          <div className="text-center mb-32 pb-20 border-b border-white/5">
            <h3 className="text-3xl font-serif text-ink mb-10">Specialized Memorial Services</h3>
            <div className="flex flex-wrap justify-center items-center gap-x-10 gap-y-6 text-[11px] uppercase tracking-[0.3em] font-bold">
              <Link to="/funeral-livestreaming-nairobi-kenya" className="text-gold hover:text-gold-hover transition-colors">Livestreaming</Link>
              <span className="w-1.5 h-1.5 rounded-full bg-white/10" />
              <Link to="/funeral-photography-nairobi-kenya" className="text-gold hover:text-gold-hover transition-colors">Photography</Link>
              <span className="w-1.5 h-1.5 rounded-full bg-white/10" />
              <Link to="/memorial-video-production-kenya" className="text-gold hover:text-gold-hover transition-colors">Video Production</Link>
              <span className="w-1.5 h-1.5 rounded-full bg-white/10" />
              <Link to="/funeral-drone-coverage-kenya" className="text-gold hover:text-gold-hover transition-colors">Drone Coverage</Link>
            </div>
          </div>

          <div className="grid lg:grid-cols-12 gap-20 mb-32">
            {/* Brand Column */}
            <div className="lg:col-span-5">
              <Link to="/" className="flex items-center gap-4 mb-10 group">
                <img
                  src="https://images.pixieset.com/492772111/2c35f543e5662c954e9dea8a7381893c-large.png"
                  alt="Faraja Media Branding"
                  width="64"
                  height="64"
                  loading="eager"
                  className="object-contain mix-blend-lighten"
                />
                <div className="flex flex-col">
                  <span className="text-3xl font-serif font-bold text-ink group-hover:text-gold transition-colors">FARAJA MEDIA</span>
                  <span className="text-xs uppercase tracking-[0.4em] text-ink2">Helping You Be Present</span>
                </div>
              </Link>
              <p className="text-ink2 max-w-md mb-12 leading-relaxed font-sans text-lg">
                Professional funeral livestreaming, photography, and memorial video services in Nairobi.
                Helping families in Kenya and the diaspora preserve and share meaningful moments with care, dignity, and absolute transparency.
              </p>
              <SocialConnect />
              <div className="mt-16">
                <a
                  href={SOCIAL_LINKS.googleReview}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-4 bg-white/[0.03] hover:bg-gold hover:text-bg px-10 py-5 rounded-full text-[11px] font-bold uppercase tracking-[0.2em] transition-all border border-white/5 group"
                >
                  <Star size={16} className="text-gold group-hover:text-bg transition-colors" /> Leave a Tribute
                </a>
              </div>
            </div>

            {/* Navigation Column */}
            <div className="lg:col-span-3">
              <h4 className="font-serif text-2xl text-ink mb-10">Navigation</h4>
              <ul className="space-y-6 text-[13px] text-ink3 uppercase tracking-[0.2em] font-bold">
                <li><Link to="/services" className="hover:text-gold transition-colors">Our Services</Link></li>
                <li><Link to="/portfolio" className="hover:text-gold transition-colors">Portfolio</Link></li>
                <li><Link to="/blog" className="hover:text-gold transition-colors">Memorial Blog</Link></li>
                <li><Link to="/about" className="hover:text-gold transition-colors">Our Story</Link></li>
                <li><Link to="/contact" className="hover:text-gold transition-colors">Get in Touch</Link></li>
              </ul>
            </div>

            {/* Contact Column */}
            <div className="lg:col-span-4">
              <h4 className="font-serif text-2xl text-ink mb-10">Contact & Support</h4>
              <ul className="space-y-8 text-lg text-ink2">
                <li className="flex items-center gap-6">
                  <div className="w-12 h-12 rounded-2xl bg-gold-bg flex items-center justify-center shrink-0 border border-gold-border">
                    <Phone size={18} className="text-gold" />
                  </div>
                  <a
                    href="tel:+254721296564"
                    onClick={() => trackEvent(ANALYTICS_EVENTS.PHONE_CLICK, { location: 'Footer' })}
                    className="hover:text-gold transition-colors font-bold text-ink"
                  >
                    +254 721 296 564
                  </a>
                </li>
                <li className="flex items-center gap-6">
                  <div className="w-12 h-12 rounded-2xl bg-gold-bg flex items-center justify-center shrink-0 border border-gold-border">
                    <MessageCircle size={18} className="text-gold" />
                  </div>
                  <a
                    href={SOCIAL_LINKS.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Chat on WhatsApp"
                    onClick={() => trackWhatsAppClick('Footer', location.pathname)}
                    className="hover:text-gold transition-colors inline-flex items-center min-w-[48px] min-h-[48px]"
                  >
                    WhatsApp Available 24/7
                  </a>
                </li>
                <li className="flex items-center gap-6">
                  <div className="w-12 h-12 rounded-2xl bg-gold-bg flex items-center justify-center shrink-0 border border-gold-border">
                    <Globe size={18} className="text-gold" />
                  </div>
                  <span>Westlands, Nairobi, Kenya</span>
                </li>
                <li className="pt-10 border-t border-white/5">
                  <div className="bg-white/[0.02] p-8 rounded-[2rem] border border-white/5 shadow-xl">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-ink2 mb-3 font-bold">M-Pesa Paybill</p>
                    <p className="text-2xl font-serif font-bold text-ink mb-1">247247</p>
                    <p className="text-sm text-gold font-bold tracking-widest uppercase">Account: 502401</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* Copyright & Legal */}
          <div className="pt-16 border-t border-white/5 flex flex-col lg:flex-row justify-between items-center gap-10 text-[10px] uppercase tracking-[0.3em] text-ink3 text-center lg:text-left font-bold">
            <p className="max-w-3xl leading-relaxed">© 2026 Faraja Media. Professional Funeral Livestreaming & Memorial Services — Nairobi, Kenya & East Africa. Serving Diaspora Families Worldwide.</p>
            <div className="flex gap-12">
              <a href="#" className="hover:text-gold transition-colors">Privacy Policy</a>
            </div>
          </div>
        </div>
      </footer>

      {/* ================= FLOATING WHATSAPP (visible on all pages except booking) ================= */}
      {location.pathname !== '/booking' && location.pathname !== '/' && (
        <a
          href={SOCIAL_LINKS.whatsapp}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Chat with us on WhatsApp"
          onClick={() => trackWhatsAppClick('FloatingFAB', location.pathname)}
          className="fixed bottom-6 right-6 w-14 h-14 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition z-50 focus:outline-none focus:ring-2 focus:ring-green-400"
          style={{ backgroundColor: '#25D366', minWidth: 48, minHeight: 48 }}
        >
          <WhatsAppIcon size={28} color="#FFFFFF" />
        </a>
      )}

      {location.pathname === '/booking' && (
        <Suspense fallback={null}>
          <AIAssistant defaultOpen={false} />
        </Suspense>
      )}

    </div>
  );
}
